import type {
  AdminUserView,
  AuditLog,
  WorkflowOutput,
  WorkflowUsersResponse,
} from "@webuild-ai/admin/models/components";

import { WeBuildAiAdmin } from "@webuild-ai/admin";
import { create } from "zustand";

import { logger } from "@/telemetry";

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

interface AdminStoreState {
  // State
  users: AdminUserView[];
  workflows: WorkflowOutput[];
  workflowUserCounts: Record<string, number>;
  auditLogs: AuditLog[];
  isLoading: boolean;
  error: string | null;

  // Users
  loadUsers: (adminClient: WeBuildAiAdmin, search?: string) => Promise<void>;
  changeUserRole: (
    adminClient: WeBuildAiAdmin,
    userId: string,
    role: "Admin" | "Manager" | "User",
  ) => Promise<void>;

  // Workflows
  loadWorkflows: (adminClient: WeBuildAiAdmin) => Promise<void>;
  loadWorkflowUsers: (
    adminClient: WeBuildAiAdmin,
    workflowId: string,
  ) => Promise<WorkflowUsersResponse>;

  // Workflow user management
  addUserToWorkflow: (
    adminClient: WeBuildAiAdmin,
    workflowId: string,
    userId: string,
    accessLevel: string,
  ) => Promise<void>;
  removeUserFromWorkflow: (
    adminClient: WeBuildAiAdmin,
    workflowId: string,
    userId: string,
  ) => Promise<void>;
  changeWorkflowUserAccess: (
    adminClient: WeBuildAiAdmin,
    workflowId: string,
    userId: string,
    accessLevel: string,
  ) => Promise<void>;

  // Audit logs
  loadAuditLogs: (
    adminClient: WeBuildAiAdmin,
    options?: { limit?: number; offset?: number; actionFilter?: string },
  ) => Promise<void>;

  // Delete
  deleteWorkflow: (adminClient: WeBuildAiAdmin, workflowId: string) => Promise<void>;

  // Bulk operations
  loadAll: (adminClient: WeBuildAiAdmin) => Promise<void>;
  bulkChangeUserRoles: (
    adminClient: WeBuildAiAdmin,
    userIds: string[],
    role: "Admin" | "Manager" | "User",
  ) => Promise<{ succeeded: string[]; failed: string[] }>;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAdminStore = create<AdminStoreState>((set, get) => ({
  users: [],
  workflows: [],
  workflowUserCounts: {},
  auditLogs: [],
  isLoading: false,
  error: null,

  loadUsers: async (adminClient: WeBuildAiAdmin, search?: string) => {
    try {
      const data = await adminClient.adminManagement.adminListUsers({
        search: search || undefined,
      });

      set({ users: data.users });
    } catch (error) {
      logger.error("Failed to fetch admin users", error);
    }
  },

  changeUserRole: async (
    adminClient: WeBuildAiAdmin,
    userId: string,
    role: "Admin" | "Manager" | "User",
  ) => {
    await adminClient.adminManagement.adminChangeUserRole({
      userId,
      changeRoleRequest: { role },
    });
  },

  loadWorkflows: async (adminClient: WeBuildAiAdmin) => {
    try {
      const [data, countsResponse] = await Promise.all([
        adminClient.workflows.listWorkflows({}),
        adminClient.adminManagement
          .adminGetWorkflowUserCounts()
          .catch(() => ({ counts: {} as Record<string, number> })),
      ]);

      set({ workflows: data, workflowUserCounts: countsResponse.counts });
    } catch (error) {
      logger.error("Failed to fetch workflows", error);
    }
  },

  loadWorkflowUsers: async (
    adminClient: WeBuildAiAdmin,
    workflowId: string,
  ): Promise<WorkflowUsersResponse> => {
    return adminClient.adminManagement.adminGetWorkflowUsers({ workflowId });
  },

  addUserToWorkflow: async (
    adminClient: WeBuildAiAdmin,
    workflowId: string,
    userId: string,
    accessLevel: string,
  ) => {
    await adminClient.adminManagement.adminAddWorkflowUser({
      workflowId,
      workflowUserRequest: { userId, accessLevel },
    });
  },

  removeUserFromWorkflow: async (
    adminClient: WeBuildAiAdmin,
    workflowId: string,
    userId: string,
  ) => {
    await adminClient.adminManagement.adminRemoveWorkflowUser({
      workflowId,
      userId,
    });
  },

  changeWorkflowUserAccess: async (
    adminClient: WeBuildAiAdmin,
    workflowId: string,
    userId: string,
    accessLevel: string,
  ) => {
    await adminClient.adminManagement.adminChangeWorkflowUserAccess({
      workflowId,
      userId,
      changeAccessLevelRequest: { accessLevel },
    });
  },

  loadAuditLogs: async (
    adminClient: WeBuildAiAdmin,
    options?: { limit?: number; offset?: number; actionFilter?: string },
  ) => {
    try {
      const data = await adminClient.adminManagement.adminListAuditLogs({
        limit: options?.limit,
        offset: options?.offset,
        actionFilter: options?.actionFilter,
      });

      set({ auditLogs: data.logs });
    } catch (error) {
      logger.error("Failed to fetch audit logs", error);
    }
  },

  deleteWorkflow: async (adminClient: WeBuildAiAdmin, workflowId: string) => {
    await adminClient.workflows.deleteWorkflow({ workflowId });
    const { workflows, workflowUserCounts } = get();
    const { [workflowId]: _, ...remainingCounts } = workflowUserCounts;

    set({
      workflows: workflows.filter((w) => w.id !== workflowId),
      workflowUserCounts: remainingCounts,
    });
  },

  loadAll: async (adminClient: WeBuildAiAdmin) => {
    set({ isLoading: true, error: null });

    try {
      const store = get();

      await Promise.all([
        store.loadUsers(adminClient),
        store.loadWorkflows(adminClient),
        store.loadAuditLogs(adminClient, { limit: 50 }),
      ]);

      // Check if all data loaded empty (individual loaders catch their own errors)
      const state = get();

      if (state.users.length === 0 && state.workflows.length === 0) {
        set({ error: "Failed to load admin data. Please try again." });
      }
    } catch (error) {
      logger.error("Failed to load admin data", error);
      set({ error: "Failed to load admin data. Please try again." });
    } finally {
      set({ isLoading: false });
    }
  },

  bulkChangeUserRoles: async (
    adminClient: WeBuildAiAdmin,
    userIds: string[],
    role: "Admin" | "Manager" | "User",
  ) => {
    const results = await Promise.allSettled(
      userIds.map((userId) =>
        adminClient.adminManagement.adminChangeUserRole({
          userId,
          changeRoleRequest: { role },
        }),
      ),
    );

    const succeeded: string[] = [];
    const failed: string[] = [];

    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        succeeded.push(userIds[index]);
      } else {
        logger.error(`Failed to change role for user ${userIds[index]}`, result.reason);
        failed.push(userIds[index]);
      }
    });

    return { succeeded, failed };
  },
}));
